/**
 * Podcast Manager (Episode Composer) — Runtime CSS variable injection
 *
 * Why this exists:
 *   Tailwind's PostCSS pipeline strips :root custom-property blocks from
 *   the child app's CSS during build.  In Module Federation the child's
 *   stylesheet is loaded by the parent shell, so those definitions never
 *   reach the browser.  Injecting via JS at mount time sidesteps the
 *   problem entirely and makes light/dark switching trivial — just swap
 *   the token map.
 *
 * Prefix: --pm-*  (podcast manager)
 */

export interface ThemeTokens {
  [key: string]: string;
}

/* ── Dark theme (current default) ────────────────────────────── */

const darkTokens: ThemeTokens = {
  // Surface tiers — darkest to lightest
  '--pm-bg-base':      '#0d1117',
  '--pm-bg-deeper':    '#0a0f12',
  '--pm-bg-card':      '#161b22',
  '--pm-bg-card-alt':  '#1c2128',
  '--pm-bg-inset':     '#1a1f26',
  '--pm-bg-hover':     '#1f2937',
  '--pm-bg-overlay':   'rgba(0, 0, 0, 0.75)',

  // Text tiers
  '--pm-text-primary':   '#f0f6fc',
  '--pm-text-secondary': '#c9d1d9',
  '--pm-text-muted':     '#8b949e',
  '--pm-text-faint':     '#6e7681',
  '--pm-text-dim':       '#484f58',

  // Borders
  '--pm-border-default':  '#30363d',
  '--pm-border-muted':    'rgba(255, 255, 255, 0.06)',
  '--pm-border-emphasis': 'rgba(255, 255, 255, 0.12)',

  // Surfaces (translucent white lifts for dark theme)
  '--pm-surface-1': 'rgba(255, 255, 255, 0.03)',
  '--pm-surface-2': 'rgba(255, 255, 255, 0.04)',
  '--pm-surface-3': 'rgba(255, 255, 255, 0.06)',
  '--pm-surface-4': 'rgba(255, 255, 255, 0.08)',

  // Brand cyan
  '--pm-cyan':               '#00d4ff',
  '--pm-cyan-text':          '#00d4ff',
  '--pm-cyan-dim':           '#00b8e6',
  '--pm-cyan-bg':            'rgba(0, 212, 255, 0.03)',
  '--pm-cyan-bg-strong':     'rgba(0, 212, 255, 0.06)',
  '--pm-cyan-border':        'rgba(0, 212, 255, 0.25)',
  '--pm-cyan-border-hover':  'rgba(0, 212, 255, 0.4)',
  '--pm-cyan-border-muted':  'rgba(0, 212, 255, 0.15)',
  '--pm-cyan-border-faint':  'rgba(0, 212, 255, 0.08)',
  '--pm-cyan-glow':          'rgba(0, 212, 255, 0.4)',

  // Toast
  '--pm-toast-bg':           '#141a1c',
  '--pm-toast-bg-hover':     '#1a2224',
  '--pm-toast-border':       '#2d3a3d',
  '--pm-toast-border-hover': '#3d4d50',
  '--pm-toast-icon-bg':      'rgba(34, 211, 238, 0.15)',

  // Semantic — green/teal
  '--pm-green':        '#3ecfb2',
  '--pm-green-dim':    '#00c8b4',
  '--pm-green-bg':     'rgba(62, 207, 178, 0.08)',

  // Semantic — amber
  '--pm-amber':        '#fbbf24',
  '--pm-amber-text':   '#fb923c',
  '--pm-amber-bg':     'rgba(251, 191, 36, 0.15)',

  // Semantic — blue
  '--pm-blue-text':    '#60a5fa',
  '--pm-blue-bg':      'rgba(96, 165, 250, 0.15)',

  // Star / favorite
  '--pm-star-active':   '#fbbf24',
  '--pm-star-inactive': '#6e7681',

  // Button — primary (cyan)
  '--pm-btn-primary-bg':       '#00d4ff',
  '--pm-btn-primary-text':     '#0d1117',
  '--pm-btn-primary-hover':    '#00b8e6',

  // Gradients for animation backgrounds
  '--pm-gradient-dark':  'linear-gradient(180deg, #0d1117 0%, #000000 100%)',

  // Gray constants for animations
  '--pm-gray-dim':   '#3a3a4a',
  '--pm-gray-text':  '#8a8a9a',
  '--pm-gray-track': '#2a2a3a',

  // Shadows
  '--pm-shadow':        'rgba(0, 0, 0, 0.35)',
  '--pm-shadow-strong': 'rgba(0, 0, 0, 0.55)',

  // Chip / badge backgrounds (for status displays)
  '--pm-chip-bg':      'rgba(0, 212, 255, 0.3)',

  // Help panel category icons (bright on dark bg)
  '--pm-help-icon-purple':  '#a78bfa',
  '--pm-help-icon-yellow':  '#fbbf24',
  '--pm-help-icon-red':     '#f87171',
  '--pm-help-icon-green':   '#34d399',

  // Podbean green
  '--pm-podbean-green':     '#80B93C',
  '--pm-podbean-green-dim': 'rgba(128, 185, 60, 0.3)',

  // ── Tailwind/shadcn overrides ──
  '--background':            '#0d1117',
  '--foreground':            '#f0f6fc',
  '--card':                  '#161b22',
  '--card-foreground':       '#f0f6fc',
  '--popover':               '#1c2128',
  '--popover-foreground':    '#f0f6fc',
  '--primary':               '#00d4ff',
  '--primary-foreground':    '#0d1117',
  '--secondary':             '#1c2128',
  '--secondary-foreground':  '#c9d1d9',
  '--muted':                 '#21262d',
  '--muted-foreground':      '#8b949e',
  '--accent':                'rgba(0, 212, 255, 0.06)',
  '--accent-foreground':     '#f0f6fc',
  '--border':                '#30363d',
  '--input':                 '#21262d',
  '--ring':                  '#00d4ff',
  '--destructive':           '#f85149',
  '--destructive-foreground':'#f0f6fc',
};

/* ── Light theme — Cyan Edge + Warm Slate ───────────────────── */

const lightTokens: ThemeTokens = {
  // Surface tiers — cool off-white base for card contrast
  '--pm-bg-base':      '#F1F5F9',
  '--pm-bg-deeper':    '#F1F5F9',
  '--pm-bg-card':      '#FFFFFF',
  '--pm-bg-card-alt':  '#F8FAFB',
  '--pm-bg-inset':     '#F1F5F9',
  '--pm-bg-hover':     'rgba(8, 145, 178, 0.04)',
  '--pm-bg-overlay':   'rgba(0, 0, 0, 0.45)',

  // Text tiers
  '--pm-text-primary':   '#0F1419',
  '--pm-text-secondary': '#1E293B',
  '--pm-text-muted':     '#64748B',
  '--pm-text-faint':     '#94A3B8',
  '--pm-text-dim':       '#CBD5E1',

  // Borders
  '--pm-border-default':  '#CBD5E1',
  '--pm-border-muted':    'rgba(0, 0, 0, 0.05)',
  '--pm-border-emphasis': 'rgba(0, 0, 0, 0.12)',

  // Surfaces (translucent black tints for light theme)
  '--pm-surface-1': 'rgba(0, 0, 0, 0.02)',
  '--pm-surface-2': 'rgba(0, 0, 0, 0.03)',
  '--pm-surface-3': 'rgba(0, 0, 0, 0.05)',
  '--pm-surface-4': 'rgba(0, 0, 0, 0.07)',

  // Brand cyan — vivid for accents, darker for readable text
  '--pm-cyan':               '#0891B2',
  '--pm-cyan-text':          '#06B6D4',
  '--pm-cyan-dim':           '#0E7490',
  '--pm-cyan-bg':            'rgba(8, 145, 178, 0.04)',
  '--pm-cyan-bg-strong':     'rgba(8, 145, 178, 0.08)',
  '--pm-cyan-border':        'rgba(8, 145, 178, 0.35)',
  '--pm-cyan-border-hover':  'rgba(8, 145, 178, 0.55)',
  '--pm-cyan-border-muted':  'rgba(8, 145, 178, 0.20)',
  '--pm-cyan-border-faint':  'rgba(8, 145, 178, 0.10)',
  '--pm-cyan-glow':          'rgba(8, 145, 178, 0.3)',

  // Toast
  '--pm-toast-bg':           '#FFFFFF',
  '--pm-toast-bg-hover':     '#F1F5F9',
  '--pm-toast-border':       '#E2E8F0',
  '--pm-toast-border-hover': '#CBD5E1',
  '--pm-toast-icon-bg':      'rgba(8, 145, 178, 0.10)',

  // Semantic — green/teal
  '--pm-green':        '#059669',
  '--pm-green-dim':    '#047857',
  '--pm-green-bg':     'rgba(5, 150, 105, 0.06)',

  // Semantic — amber
  '--pm-amber':        '#d97706',
  '--pm-amber-text':   '#ea580c',
  '--pm-amber-bg':     'rgba(217, 119, 6, 0.08)',

  // Semantic — blue
  '--pm-blue-text':    '#2563eb',
  '--pm-blue-bg':      'rgba(37, 99, 235, 0.08)',

  // Star / favorite
  '--pm-star-active':   '#f59e0b',
  '--pm-star-inactive': '#94A3B8',

  // Button — primary (cyan)
  '--pm-btn-primary-bg':       '#0891B2',
  '--pm-btn-primary-text':     '#FFFFFF',
  '--pm-btn-primary-hover':    '#0E7490',

  // Gradients for animation backgrounds
  '--pm-gradient-dark':  'linear-gradient(180deg, #F8FAFB 0%, #F1F5F9 100%)',

  // Gray constants for animations (adapted for light)
  '--pm-gray-dim':   '#CBD5E1',
  '--pm-gray-text':  '#64748B',
  '--pm-gray-track': '#E2E8F0',

  // Shadows — subtle for card lift on cool off-white
  '--pm-shadow':        'rgba(0, 0, 0, 0.08)',
  '--pm-shadow-strong': 'rgba(0, 0, 0, 0.16)',

  // Chip / badge backgrounds
  '--pm-chip-bg':      'rgba(8, 145, 178, 0.12)',

  // Help panel category icons (darker for light bg readability)
  '--pm-help-icon-purple':  '#7c3aed',
  '--pm-help-icon-yellow':  '#b45309',
  '--pm-help-icon-red':     '#DC2626',
  '--pm-help-icon-green':   '#059669',

  // Podbean green
  '--pm-podbean-green':     '#5a9a1f',
  '--pm-podbean-green-dim': 'rgba(90, 154, 31, 0.2)',

  // ── Tailwind/shadcn overrides ──
  '--background':            '#F1F5F9',
  '--foreground':            '#0F1419',
  '--card':                  '#FFFFFF',
  '--card-foreground':       '#0F1419',
  '--popover':               '#FFFFFF',
  '--popover-foreground':    '#0F1419',
  '--primary':               '#0891B2',
  '--primary-foreground':    '#FFFFFF',
  '--secondary':             '#E2E8F0',
  '--secondary-foreground':  '#1E293B',
  '--muted':                 '#E2E8F0',
  '--muted-foreground':      '#64748B',
  '--accent':                '#F0FDFA',
  '--accent-foreground':     '#0F1419',
  '--border':                '#CBD5E1',
  '--input':                 '#CBD5E1',
  '--ring':                  '#0891B2',
  '--destructive':           '#DC2626',
  '--destructive-foreground':'#FFFFFF',
};

/* ── Theme types ─────────────────────────────────────────────── */

export type ThemeMode = 'dark' | 'light';

const THEME_MAPS: Record<ThemeMode, ThemeTokens> = {
  dark: darkTokens,
  light: lightTokens,
};

/* ── Injection ───────────────────────────────────────────────── */

const STYLE_ID = 'pm-theme-tokens';

/**
 * Inject Podcast Manager CSS custom properties into the document head.
 * Safe to call multiple times — re-uses the same <style> element.
 */
export function injectTheme(mode: ThemeMode = 'dark'): void {
  const tokens = THEME_MAPS[mode];
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;

  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }

  const declarations = Object.entries(tokens)
    .map(([prop, value]) => `  ${prop}: ${value};`)
    .join('\n');

  style.textContent = `:root, .parent-app-scope {\n${declarations}\n}`;
}

/** Remove the injected theme (useful for cleanup in tests). */
export function removeTheme(): void {
  document.getElementById(STYLE_ID)?.remove();
}

export { darkTokens, lightTokens };
